import { useState, useEffect, useMemo } from 'react';
import { Search, MapPin, AlertTriangle, Bell, Calendar, X } from 'lucide-react';
import { MapView } from '../../components/map/MapView';
import { VehicleListItem } from '../../components/vehicles/VehicleListItem';
import { VehicleDetailPanel } from '../../components/vehicles/VehicleDetailPanel';
import { VehicleHistoryControls } from '../../components/monitoring/VehicleHistoryControls';
import { HistoryPlaybackPanel } from '../../components/monitoring/HistoryPlaybackPanel';
import { Spinner } from '../../components/ui/Spinner';
import { Input } from '../../components/ui/Input';
import { useVehicles } from '../../hooks/useVehicles';
import { useRouteHistorySession } from '../../hooks/useRouteHistorySession';
import { useLogbookEntries } from '../../hooks/useLogbookEntries';
import { rangoDeFiltro } from '../../utils/logbook';
import { DUE_SOON_DAYS, OIL_DUE_SOON_KM, vehicleMaintenance } from '../../utils/maintenance';
import { NAV_ROUTES } from '../../utils/constants';
import type { Vehicle } from '../../types/vehicle';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

const STATUS_FILTERS: { value: Vehicle['status'] | 'all'; label: string }[] = [
  { value: 'all', label: 'Todos' },
  { value: 'critical_event', label: 'Evento Crítico' },
  { value: 'no_signal', label: 'Sin Señal' },
  { value: 'in_route', label: 'En Ruta' },
  { value: 'stopped', label: 'Detenidos' },
  { value: 'offline', label: 'Offline' },
];

export default function MonitoringPage() {
  const { vehicles, isLoading, selectedVehicle, selectVehicle } = useVehicles();
  // FE-13 — las novedades de HOY, reales. Antes esto salía de un contexto en
  // memoria que solo veía lo registrado en esta misma pestaña: el banner
  // nunca mostró la novedad de otra persona. `rangoDeFiltro('today')` filtra
  // por `occurredAt` —cuándo pasó—, así que una varada de anteayer relatada
  // esta mañana no aparece aquí: pertenece a anteayer.
  const filtroHoy = useMemo(() => rangoDeFiltro('today'), []);
  const { entries: novedadesHoy } = useLogbookEntries(filtroHoy);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Vehicle['status'] | 'all'>('all');
  const [showPanel, setShowPanel] = useState(false);
  // Solo afecta al bottom-sheet móvil (CSS, @media max-width:900px) — en
  // desktop .monitoring-page__detail es la columna lateral fija de siempre,
  // muestra todo el contenido sin importar este estado.
  const [isDetailExpanded, setIsDetailExpanded] = useState(false);
  const [showNoveltiesBanner, setShowNoveltiesBanner] = useState(true);
  const [showExpiredBanner, setShowExpiredBanner] = useState(true);
  const [showDueSoonBanner, setShowDueSoonBanner] = useState(true);

  // FE-25/26 — histórico de recorrido: capa aparte del mapa, independiente
  // de la selección de vehículo en vivo (`selectedVehicle` es la del panel
  // lateral/clic en el marcador; esta es la del formulario de consulta).
  // FE-27: consulta + reproducción + paradas + distancia viven en un hook
  // compartido con la página propia de mobile.
  const history = useRouteHistorySession();
  const { playback } = history;
  // FE-27 (2026-09-20) — el panel de reproducción es el mismo de mobile, acoplado
  // bajo el mapa. "Consulta" abre el formulario flotante (`historyFormOpen`, que
  // controla `VehicleHistoryControls`); "Paradas" oculta/muestra los marcadores.
  const [historyFormOpen, setHistoryFormOpen] = useState(false);
  const [showStops, setShowStops] = useState(true);
  const showPlayback = !!history.route && playback.canPlay;
  const historyPlate = vehicles.find(v => v.id === history.vehicleId)?.plate ?? null;
  const handleRestart = () => {
    playback.seek(playback.startMs);
    playback.play();
  };

  useEffect(() => {
    document.title = 'Monitoreo | Paitron Radar';
  }, []);

  // FE-23 — vencimientos. El estado sale de `utils/maintenance.ts` (única
  // definición del umbral). Dos listas, no una: un documento YA VENCIDO y uno
  // por vencer no piden lo mismo, y el banner viejo los mezclaba (contaba el
  // vencido como "próximo a vencer"). Cada placa lleva QUÉ vence, no solo que
  // "algo" vence.
  const maintenanceAlerts = useMemo(() => {
    const expired: { plate: string; what: string[] }[] = [];
    const dueSoon: { plate: string; what: string[] }[] = [];
    for (const v of vehicles) {
      const { items } = vehicleMaintenance(v);
      const expiredWhat = items.filter(i => i.status === 'expired').map(i => i.label);
      const dueSoonWhat = items.filter(i => i.status === 'due_soon').map(i => i.label);
      if (expiredWhat.length > 0) expired.push({ plate: v.plate, what: expiredWhat });
      if (dueSoonWhat.length > 0) dueSoon.push({ plate: v.plate, what: dueSoonWhat });
    }
    return { expired, dueSoon };
  }, [vehicles]);
  const describe = (list: { plate: string; what: string[] }[]) =>
    list.map(x => `${x.plate} (${x.what.join(', ')})`).join(', ');

  const filtered = vehicles.filter(v => {
    const matchSearch = search === '' ||
      v.plate.toLowerCase().includes(search.toLowerCase()) ||
      (v.driverName ?? '').toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || v.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const handleVehicleSelect = (vehicle: Vehicle) => {
    selectVehicle(vehicle.id);
    setShowPanel(true);
    setIsDetailExpanded(false);
  };

  const handleClosePanel = () => {
    setShowPanel(false);
    selectVehicle(null);
    setIsDetailExpanded(false);
  };

  const counts: Record<string, number> = { all: vehicles.length };
  vehicles.forEach(v => { counts[v.status] = (counts[v.status] ?? 0) + 1; });

  return (
    <div className="monitoring-page" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      
      {/* Supervisor Alerts & Banners Bar */}
      {(maintenanceAlerts.expired.length > 0 && showExpiredBanner) || (maintenanceAlerts.dueSoon.length > 0 && showDueSoonBanner) || (novedadesHoy.length > 0 && showNoveltiesBanner) ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {/* FE-13 — novedades reales del día. No hay "marcar leído": el
              contrato no tiene estado ni resolución de novedad (ADR-015 §49
              lista sus campos y los deja fuera a propósito), así que un botón
              que dijera "leído" estaría inventando un dato que no se guarda en
              ninguna parte. Se cierra el banner, que es lo que hace de verdad. */}
          {novedadesHoy.length > 0 && showNoveltiesBanner && (
            <div style={{ background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 'var(--radius-lg)', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', color: '#8B5CF6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                <Bell size={18} />
                <span style={{ fontSize: '13px', fontWeight: 600 }}>
                  <strong>Novedades de hoy ({novedadesHoy.length}):</strong>{' '}
                  {novedadesHoy[0].driverName ?? 'sin conductor'}
                  {novedadesHoy[0].vehiclePlate ? ` (${novedadesHoy[0].vehiclePlate})` : ''} — {novedadesHoy[0].entryTypeLabel}: “{novedadesHoy[0].description}”
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Link to={NAV_ROUTES.LOGBOOK} style={{ background: '#8B5CF6', color: '#FFF', borderRadius: '4px', padding: '3px 10px', fontSize: '11px', fontWeight: 700, textDecoration: 'none' }}>
                  Ver novedades
                </Link>
                <button onClick={() => setShowNoveltiesBanner(false)} style={{ background: 'none', border: 'none', color: '#8B5CF6', cursor: 'pointer' }}><X size={16} /></button>
              </div>
            </div>
          )}

          {/* Vencimientos: vencidos (rojo) y por vencer (ámbar), cada uno con su aviso. */}
          {maintenanceAlerts.expired.length > 0 && showExpiredBanner && (
            <div id="maintenance-banner-expired" style={{ background: 'var(--critical-bg)', border: '1px solid var(--critical)', borderRadius: 'var(--radius-lg)', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', color: 'var(--critical)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                <Calendar size={18} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '13px', fontWeight: 600 }}>
                  <strong>Vencidos ({maintenanceAlerts.expired.length}):</strong> {describe(maintenanceAlerts.expired)}.
                </span>
              </div>
              <button aria-label="Cerrar aviso de vencidos" onClick={() => setShowExpiredBanner(false)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}><X size={16} /></button>
            </div>
          )}
          {maintenanceAlerts.dueSoon.length > 0 && showDueSoonBanner && (
            <div id="maintenance-banner-due-soon" style={{ background: 'var(--medium-bg)', border: '1px solid var(--medium)', borderRadius: 'var(--radius-lg)', padding: '0.75rem 1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', color: 'var(--medium)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', minWidth: 0 }}>
                <Calendar size={18} style={{ flexShrink: 0 }} />
                <span style={{ fontSize: '13px', fontWeight: 600 }}>
                  <strong>Por vencer ({maintenanceAlerts.dueSoon.length}):</strong> {describe(maintenanceAlerts.dueSoon)} — en {DUE_SOON_DAYS} días o menos (el aceite, a menos de {OIL_DUE_SOON_KM.toLocaleString('es-CO')} km).
                </span>
              </div>
              <button aria-label="Cerrar aviso de por vencer" onClick={() => setShowDueSoonBanner(false)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}><X size={16} /></button>
            </div>
          )}
        </div>
      ) : null}

      {/* --vehicle-open: en móvil (≤700px) esconde la lista y deja mapa +
          bottom-sheet; en desktop el CSS no lo usa. */}
      <div className={clsx('monitoring-page__body', showPanel && selectedVehicle && 'monitoring-page__body--vehicle-open')}>
        {/* Left panel: vehicle list */}
        <div className="monitoring-page__sidebar">
          <div className="monitoring-page__sidebar-header">
            <Input
              id="monitoring-search"
              placeholder="Buscar placa o conductor..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              leftIcon={<Search size={15} />}
            />

            <div className="monitoring-page__filters">
              {STATUS_FILTERS.map(f => (
                <button
                  key={f.value}
                  id={`filter-${f.value}`}
                  className={clsx('monitoring-page__filter-chip', statusFilter === f.value && 'monitoring-page__filter-chip--active')}
                  onClick={() => setStatusFilter(f.value)}
                >
                  {f.label}
                  {counts[f.value] !== undefined && (
                    <span className="monitoring-page__filter-count">{counts[f.value]}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="monitoring-page__vehicle-list">
            {isLoading ? (
              <div className="monitoring-page__loading">
                <Spinner size="md" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="monitoring-page__empty">
                <MapPin size={32} />
                <p>Sin vehículos</p>
              </div>
            ) : (
              filtered.map(v => (
                <VehicleListItem
                  key={v.id}
                  vehicle={v}
                  isSelected={selectedVehicle?.id === v.id}
                  onClick={() => handleVehicleSelect(v)}
                />
              ))
            )}
          </div>

          <div className="monitoring-page__summary">
            <span>{vehicles.filter(v => v.status === 'in_route').length} en ruta</span>
            <span>·</span>
            <span className="monitoring-page__summary-critical">
              <AlertTriangle size={12} />
              {vehicles.filter(v => v.status === 'critical_event').length} críticos
            </span>
            <span>·</span>
            <span>{vehicles.length} total</span>
          </div>
        </div>

        {/* Map */}
        <div className="monitoring-page__map">
          {!isLoading && (
            <VehicleHistoryControls
              vehicles={vehicles}
              route={history.route}
              isLoading={history.isLoading}
              error={history.error}
              hasActiveQuery={history.hasQuery}
              onQuery={history.query}
              onClear={history.clear}
              isOpen={historyFormOpen}
              onOpenChange={setHistoryFormOpen}
            />
          )}
          {isLoading ? (
            <div className="monitoring-page__map-loading">
              <Spinner size="lg" />
              <p>Cargando mapa...</p>
            </div>
          ) : (
            <div className="monitoring-page__map-view">
              <MapView
                vehicles={filtered}
                selectedVehicle={selectedVehicle}
                historyRoute={history.route}
                playbackPosition={history.position}
                stops={showStops ? history.stops : []}
                following={history.following}
                onStopFollowing={history.stopFollowing}
                onVehicleClick={handleVehicleSelect}
              />
            </div>
          )}
          {/* FE-27 — el mismo panel de reproducción que en mobile, ACOPLADO bajo el
              mapa (no flotando): así no compite con la leyenda ni con el selector
              de estilo, y el mapa cede solo el alto que el panel ocupa. */}
          {!isLoading && showPlayback && (
            <HistoryPlaybackPanel
              playback={playback}
              position={history.position}
              distanceKm={history.distanceKm}
              downsampled={!!history.route?.downsampled}
              stopsCount={history.stops.length}
              showStops={showStops}
              vehicleLabel={historyPlate}
              onToggleStops={() => setShowStops(v => !v)}
              isFollowing={history.following}
              onToggleFollow={history.toggleFollow}
              onRestart={handleRestart}
              onEditQuery={() => setHistoryFormOpen(true)}
              onClose={history.clear}
            />
          )}
        </div>

        {/* Detail panel — columna lateral en desktop, bottom-sheet en mobile (CSS).
            En mobile arranca "peek" (chico, mapa visible) y se expande
            arrastrando o tocando el handle/header — el backdrop oscuro solo
            aparece expandido, para no tapar el mapa en el estado por defecto. */}
        {showPanel && selectedVehicle && (
          <>
            {isDetailExpanded && (
              <div className="monitoring-page__detail-backdrop" onClick={handleClosePanel} />
            )}
            <div
              className={clsx('monitoring-page__detail', isDetailExpanded && 'monitoring-page__detail--expanded')}
            >
              <VehicleDetailPanel
                vehicle={selectedVehicle}
                isExpanded={isDetailExpanded}
                onSetExpanded={setIsDetailExpanded}
                onClose={handleClosePanel}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}
